import React, { useState, useEffect } from 'react';
import './App.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

function App() {
  const [tournaments, setTournaments] = useState([]);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [analysisType, setAnalysisType] = useState('correlation');
  const [yearRange, setYearRange] = useState(5);
  const [useMachineLearning, setUseMachineLearning] = useState(true);
  const [loading, setLoading] = useState(false);
  const [courseStats, setCourseStats] = useState(null);
  const [hardRockOdds, setHardRockOdds] = useState(null);
  const [mlPredictions, setMlPredictions] = useState([]);
  const [evRankings, setEvRankings] = useState([]);
  
  // NEW: Market filtering states
  const [availableMarkets, setAvailableMarkets] = useState([]);
  const [selectedMarket, setSelectedMarket] = useState('outrights');

  useEffect(() => {
    fetchTournaments();
    fetchAvailableMarkets();
  }, []);

  const fetchTournaments = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/tournaments`);
      const data = await response.json();
      setTournaments(data.tournaments || mockTournaments);
    } catch (error) {
      console.error('Error fetching tournaments:', error);
      setTournaments(mockTournaments);
    } finally {
      setLoading(false);
    }
  };

  // NEW: Fetch available markets
  const fetchAvailableMarkets = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/available-markets`);
      const markets = await response.json();
      setAvailableMarkets(markets);
    } catch (error) {
      console.error('Error fetching markets:', error);
      setAvailableMarkets([
        { key: 'outrights', name: 'Tournament Winner' },
        { key: 'top_5_finish', name: 'Top 5 Finish' },
        { key: 'top_10_finish', name: 'Top 10 Finish' }
      ]);
    }
  };

  const analyzeTournament = async () => {
    if (!selectedTournament) return;

    setLoading(true);
    try {
      // Fetch course stats
      const statsResponse = await fetch(
        `${API_BASE_URL}/course-stats/${selectedTournament.id}?years=${yearRange}&analysisType=${analysisType}`
      );
      const stats = await statsResponse.json();
      setCourseStats(stats);

      // UPDATED: Fetch Hard Rock Bet odds with market filter
      const oddsResponse = await fetch(`${API_BASE_URL}/hardrock-odds?market=${selectedMarket}`);
      const odds = await oddsResponse.json();
      setHardRockOdds(odds);

      // Get ML predictions if enabled
      let mlPreds = [];
      if (useMachineLearning) {
        try {
          const mlResponse = await fetch(`${API_BASE_URL}/ml-predictions`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              tournamentId: selectedTournament.id,
              courseStats: stats,
              playerStats: stats.currentField || []
            })
          });
          mlPreds = await mlResponse.json();
          setMlPredictions(mlPreds);
        } catch (error) {
          console.error('ML prediction error:', error);
          // Continue without ML predictions
        }
      }

      // Calculate EV
      const evResponse = await fetch(`${API_BASE_URL}/calculate-ev`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          courseStats: stats,
          hardRockOdds: odds,
          analysisType,
          mlPredictions: mlPreds
        })
      });
      const evData = await evResponse.json();
      setEvRankings(evData);
    } catch (error) {
      console.error('Analysis error:', error);
      // Use mock data for demonstration
      setCourseStats(mockCourseStats);
      setEvRankings(mockEvRankings);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app">
      <div className="grain-overlay"></div>
      
      <header className="header">
        <div className="header-content">
          <h1 className="logo">
            <span className="logo-icon">⛳</span>
            RLHSTATS<span className="logo-accent">.COM</span>
          </h1>
          <p className="tagline">Data-Driven Golf Betting Analytics</p>
        </div>
      </header>

      <main className="main-content">
        <div className="control-panel">
          <div className="control-grid">
            <div className="control-group">
              <label>Tournament</label>
              <select
                value={selectedTournament?.id || ''}
                onChange={(e) => {
                  const tournament = tournaments.find(t => t.id === e.target.value);
                  setSelectedTournament(tournament);
                }}
                className="select-input"
              >
                <option value="">Select a tournament</option>
                {tournaments && tournaments.length > 0 && tournaments.map(tournament => (
                  <option key={tournament.id} value={tournament.id}>
                    {tournament.name}
                  </option>
                ))}
              </select>
            </div>

            {/* NEW: Market Type Selector */}
            <div className="control-group">
              <label>Betting Market</label>
              <select
                value={selectedMarket}
                onChange={(e) => setSelectedMarket(e.target.value)}
                className="select-input"
              >
                {availableMarkets.map(market => (
                  <option key={market.key} value={market.key}>
                    {market.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="control-group">
              <label>Analysis Method</label>
              <select
                value={analysisType}
                onChange={(e) => setAnalysisType(e.target.value)}
                className="select-input"
              >
                <option value="correlation">Correlation Analysis</option>
                <option value="strokesGained">Strokes Gained Focus</option>
                <option value="historical">Historical Performance</option>
                <option value="regression">Regression Model</option>
              </select>
            </div>

            <div className="control-group">
              <label>Historical Data: {yearRange} Years</label>
              <input
                type="range"
                min="1"
                max="10"
                value={yearRange}
                onChange={(e) => setYearRange(parseInt(e.target.value))}
                className="range-input"
              />
            </div>

            <div className="control-group">
              <label className="ml-toggle-label">
                <input
                  type="checkbox"
                  checked={useMachineLearning}
                  onChange={(e) => setUseMachineLearning(e.target.checked)}
                  className="ml-toggle-checkbox"
                />
                <span className="ml-toggle-text">
                  🤖 Use Machine Learning
                  {useMachineLearning && <span className="ml-badge">ML Active</span>}
                </span>
              </label>
            </div>

            <button
              onClick={analyzeTournament}
              disabled={!selectedTournament || loading}
              className="analyze-button"
            >
              {loading ? 'Analyzing...' : 'Run Analysis'}
            </button>
          </div>
        </div>

        {courseStats && courseStats.predictiveStats && (
          <div className="results-container">
            <section className="stats-section">
              <h2 className="section-title">
                <span className="title-number">01</span>
                Most Predictive Stats
              </h2>
              <div className="stats-grid">
                {courseStats.predictiveStats.map((stat, index) => (
                  <div key={index} className="stat-card" style={{ animationDelay: `${index * 100}ms` }}>
                    <div className="stat-rank">#{index + 1}</div>
                    <h3 className="stat-name">{stat.name}</h3>
                    <div className="stat-metrics">
                      <div className="metric">
                        <span className="metric-label">Correlation</span>
                        <span className="metric-value">{(stat.correlation * 100).toFixed(1)}%</span>
                      </div>
                      <div className="correlation-bar">
                        <div
                          className="correlation-fill"
                          style={{ width: `${stat.correlation * 100}%` }}
                        ></div>
                      </div>
                      <div className={`importance-badge importance-${stat.importance.toLowerCase()}`}>
                        {stat.importance} Impact
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="rankings-section">
              <h2 className="section-title">
                <span className="title-number">02</span>
                Expected Value Rankings - {availableMarkets.find(m => m.key === selectedMarket)?.name || 'Tournament Winner'}
              </h2>
              <div className="rankings-table">
                <div className="table-header">
                  <div className="header-cell rank-col">Rank</div>
                  <div className="header-cell player-col">Player</div>
                  <div className="header-cell">Best Odds</div>
                  {useMachineLearning && <div className="header-cell">ML Win Prob</div>}
                  {useMachineLearning && <div className="header-cell">Confidence</div>}
                  <div className="header-cell">Market Prob.</div>
                  <div className="header-cell">Expected Value</div>
                  <div className="header-cell">Recommendation</div>
                </div>
                {evRankings && evRankings.length > 0 && evRankings.map((golfer, index) => (
                  <div key={index} className="table-row" style={{ animationDelay: `${index * 80}ms` }}>
                    <div className="table-cell rank-col">
                      <span className={`rank-badge rank-${index + 1}`}>{index + 1}</span>
                    </div>
                    <div className="table-cell player-col">
                      <span className="player-name">{golfer.name}</span>
                    </div>
                    <div className="table-cell">
                      <span className="odds-value">{golfer.hardRockOdds > 0 ? `+${golfer.hardRockOdds}` : golfer.hardRockOdds}</span>
                      {golfer.bookmaker && (
                        <div style={{fontSize: '0.75em', opacity: 0.6, marginTop: '2px'}}>
                          {golfer.bookmaker}
                        </div>
                      )}
                    </div>
                    {useMachineLearning && (
                      <div className="table-cell">
                        <span className="ml-prob-value">
                          {golfer.mlWinProbability ? (golfer.mlWinProbability * 100).toFixed(1) : '0.0'}%
                        </span>
                      </div>
                    )}
                    {useMachineLearning && (
                      <div className="table-cell">
                        <div className="confidence-bar">
                          <div
                            className="confidence-fill"
                            style={{ width: `${((golfer.confidence || 0.5) * 100)}%` }}
                          ></div>
                        </div>
                      </div>
                    )}
                    <div className="table-cell">
                      <span className="prob-value">
                        {((golfer.marketProbability || golfer.impliedProbability || 0) * 100).toFixed(1)}%
                      </span>
                    </div>
                    <div className="table-cell">
                      <span className={`ev-value ${golfer.expectedValue > 10 ? 'ev-high' : golfer.expectedValue > 5 ? 'ev-medium' : 'ev-low'}`}>
                        +{(golfer.expectedValue || 0).toFixed(1)}%
                      </span>
                    </div>
                    <div className="table-cell">
                      <span className={`recommendation-badge rec-${(golfer.recommendation || 'none').toLowerCase().replace(' ', '-')}`}>
                        {golfer.recommendation || 'N/A'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="methodology-section">
              <h3 className="methodology-title">Methodology</h3>
              <p className="methodology-text">
                This analysis identifies the three most statistically significant performance indicators
                for the selected course based on {yearRange} years of historical data. Expected value (EV)
                calculations compare each golfer's statistical profile against current Hard Rock Bet odds for the 
                "{availableMarkets.find(m => m.key === selectedMarket)?.name || 'selected'}" market to identify
                profitable betting opportunities. Positive EV indicates favorable odds relative to predicted
                performance.
              </p>
            </section>
          </div>
        )}

        {!courseStats && !loading && (
          <div className="empty-state">
            <div className="empty-icon">📊</div>
            <h3>Ready to Analyze</h3>
            <p>Select a tournament, betting market, and analysis method to get started</p>
          </div>
        )}
      </main>
    </div>
  );
}

// Mock data for development/demonstration
const mockTournaments = [
  { id: 'the-masters-2025', name: 'The Masters 2025' },
  { id: 'pga-championship-2025', name: 'PGA Championship 2025' },
  { id: 'us-open-2025', name: 'U.S. Open 2025' },
  { id: 'the-open-2025', name: 'The Open Championship 2025' }
];

const mockCourseStats = {
  predictiveStats: [
    { name: 'Strokes Gained: Approach', correlation: 0.72, importance: 'High' },
    { name: 'Strokes Gained: Off-the-Tee', correlation: 0.68, importance: 'High' },
    { name: 'Greens in Regulation %', correlation: 0.65, importance: 'Medium' }
  ]
};

const mockEvRankings = [
  {
    name: 'Scottie Scheffler',
    mlWinProbability: 0.15,
    marketProbability: 0.154,
    impliedProbability: 0.154,
    confidence: 0.88,
    hardRockOdds: 550,
    decimalOdds: 6.5,
    expectedValue: 12.5,
    recommendation: 'Strong Value'
  },
  {
    name: 'Rory McIlroy',
    mlWinProbability: 0.13,
    marketProbability: 0.125,
    impliedProbability: 0.125,
    confidence: 0.82,
    hardRockOdds: 700,
    decimalOdds: 8.0,
    expectedValue: 8.3,
    recommendation: 'Moderate Value'
  },
  {
    name: 'Jon Rahm',
    mlWinProbability: 0.11,
    marketProbability: 0.111,
    impliedProbability: 0.111,
    confidence: 0.75,
    hardRockOdds: 800,
    decimalOdds: 9.0,
    expectedValue: 5.7,
    recommendation: 'Fair Value'
  }
];

export default App;
